import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ExportButton } from '@/components/common/ExportButton';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { CoveragePanel } from '@/pages/anatel/CoveragePanel';
import { FineDistributionPanel } from '@/pages/anatel/FineDistributionPanel';
import { OutcomeMixPanel } from '@/pages/anatel/OutcomeMixPanel';
import { OutcomeTrendPanel } from '@/pages/anatel/OutcomeTrendPanel';
import { fetchAnatelOverview } from '@/services/anatel';

export function AnatelOverviewPage() {
  const { t, i18n } = useTranslation(['anatel', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['anatel', 'overview'],
    queryFn: fetchAnatelOverview,
  });

  const number = new Intl.NumberFormat(i18n.language);

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  const stats = [
    { key: 'processes', value: data.corpus.processes },
    { key: 'decisions', value: data.corpus.decisions },
    { key: 'finalDecisions', value: data.corpus.final_decisions },
    { key: 'entities', value: data.corpus.entities },
  ];

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('anatel:overview.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('anatel:overview.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="anatel:overview.explainer" />

      <div className="flex justify-end">
        <ExportButton path="/api/v1/anatel/export/overview" />
      </div>

      <AsOfStrip asOf={data.as_of} />

      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.key} className="rounded-lg border bg-card p-3">
            <dt className="text-xs text-muted-foreground">
              {t(`anatel:overview.corpus.${stat.key}`)}
            </dt>
            <dd className="mt-1 text-xl font-semibold tabular-nums text-foreground">
              {number.format(stat.value)}
            </dd>
          </div>
        ))}
      </dl>

      <FieldGuide i18nKey="anatel:overview.guide.corpus" level={2} />

      {/* Loaded separately and rendered above the analytics, because the first
          thing a reader needs to know is what this corpus is a corpus OF. */}
      <CoveragePanel />

      <div className="grid gap-4 lg:grid-cols-2">
        <OutcomeMixPanel answer={data.outcome_mix} />
        <FineDistributionPanel answer={data.fine_distribution} />
      </div>

      <FieldGuide i18nKey="anatel:overview.guide.analytics" level={2} />

      <OutcomeTrendPanel answer={data.outcome_trend} />

      {/* Dispositions, NOT fine reductions. The blueprint's "appeals with
          reduction" needs an appeal linked to the decision it appeals, and that
          succeeds too rarely to divide by -- so that screen has no substrate and
          this weaker, real one takes its place. How rarely is NOT written here:
          `helpParams` below passes the measured pair to the caption, which is
          the only place either number should appear. */}
      <OutcomeMixPanel
        answer={data.appeal_dispositions}
        titleKey="anatel:overview.appeals.title"
        helpKey="anatel:overview.appeals.help"
        helpParams={{
          linked: data.appeals_linked_to_parent,
          total: data.appeal_dispositions.population.total,
        }}
        multiLabelKey="anatel:overview.appeals.multiLabel"
      />
    </div>
  );
}
